import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Home, TrendingUp, Eye, Mail } from 'lucide-react';
import { NotificationManager } from 'react-notifications';

import PropertyCard from '../../Components/LandlordProperty/PropertyCard';
import DeleteConfirmationModal from '../../Components/LandlordProperty/DeleteConfirmationModal';
import landlordPropertyService from '../../services/landlordPropertyService';
import { PROPERTY_STATUSES } from '../../constants/propertyConstants';

/**
 * PropertyDashboard Component
 * Main dashboard for landlords to manage their properties
 */
const PropertyDashboard = () => {
    const navigate = useNavigate();

    const [properties, setProperties] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, property: null });
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        loadProperties();
    }, [statusFilter]);

    useEffect(() => {
        filterProperties();
    }, [properties, searchQuery]);

    const loadProperties = async () => {
        setLoading(true);
        try {
            const filter = statusFilter === 'all' ? null : statusFilter;
            const data = await landlordPropertyService.getAllProperties(filter);
            setProperties(data.properties || []);
        } catch (error) {
            console.error('Error loading properties:', error);
            NotificationManager.error('Failed to load properties', 'Error');
        } finally {
            setLoading(false);
        }
    };

    const filterProperties = () => {
        let filtered = [...properties];

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(property =>
                (property.propertyName || property.property_name || '').toLowerCase().includes(query) ||
                (property.city || property.location || '').toLowerCase().includes(query) ||
                (property.neighborhood || '').toLowerCase().includes(query)
            );
        }

        setFilteredProperties(filtered);
    };

    const handleCreateProperty = () => {
        navigate('/landlord/properties/create');
    };

    const handleEditProperty = (propertyId) => {
        navigate(`/landlord/properties/edit/${propertyId}`);
    };

    const handleDeleteProperty = (propertyId) => {
        const property = properties.find(p => (p.propertyId || p.property_id) === propertyId);
        setDeleteModal({ isOpen: true, property });
    };

    const confirmDelete = async () => {
        if (!deleteModal.property) return;

        setIsDeleting(true);
        try {
            const propertyId = deleteModal.property.propertyId || deleteModal.property.property_id;
            await landlordPropertyService.deleteProperty(propertyId);
            NotificationManager.success('Property deleted successfully', 'Success');
            setDeleteModal({ isOpen: false, property: null });
            loadProperties();
        } catch (error) {
            console.error('Error deleting property:', error);
            NotificationManager.error('Failed to delete property', 'Error');
        } finally {
            setIsDeleting(false);
        }
    };

    const handlePublishProperty = async (propertyId) => {
        try {
            await landlordPropertyService.publishProperty(propertyId);
            NotificationManager.success('Property published successfully', 'Success');
            loadProperties();
        } catch (error) {
            console.error('Error publishing property:', error);
            NotificationManager.error('Failed to publish property', 'Error');
        }
    };

    // Calculate stats
    const stats = {
        total: properties.length,
        active: properties.filter(p => (p.status || p.listing_status) === 'active').length,
        draft: properties.filter(p => (p.status || p.listing_status) === 'draft').length,
        totalViews: properties.reduce((sum, p) => sum + (p.viewCount || p.views_count || 0), 0),
        totalInquiries: properties.reduce((sum, p) => sum + (p.inquiryCount || p.inquiries_count || 0), 0)
    };

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="font-display text-3xl font-bold text-soil-900">My Properties</h1>
                            <p className="mt-2 text-soil-600">
                                Manage and track your property listings
                            </p>
                        </div>
                        <button
                            onClick={handleCreateProperty}
                            className="flex items-center px-4 py-2 bg-highland-500 text-white rounded-lg font-medium hover:bg-highland-600 transition-colors"
                        >
                            <Plus className="h-5 w-5 mr-2" />
                            Add Property
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Total Properties</p>
                                <p className="text-2xl font-bold text-soil-900 mt-1">{stats.total}</p>
                            </div>
                            <div className="p-3 bg-highland-50 rounded-lg">
                                <Home className="h-6 w-6 text-highland-500" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Active</p>
                                <p className="font-mono text-2xl font-bold text-highland-600 mt-1">{stats.active}</p>
                            </div>
                            <div className="p-3 bg-highland-50 rounded-lg">
                                <TrendingUp className="h-6 w-6 text-highland-500" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Drafts</p>
                                <p className="font-mono text-2xl font-bold text-dawn-600 mt-1">{stats.draft}</p>
                            </div>
                            <div className="p-3 bg-dawn-100/40 rounded-lg">
                                <Filter className="h-6 w-6 text-dawn-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Total Views</p>
                                <p className="font-mono text-2xl font-bold text-soil-900 mt-1">{stats.totalViews}</p>
                            </div>
                            <div className="p-3 bg-jacaranda-100 rounded-lg">
                                <Eye className="h-6 w-6 text-jacaranda-500" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Inquiries</p>
                                <p className="font-mono text-2xl font-bold text-soil-900 mt-1">{stats.totalInquiries}</p>
                            </div>
                            <div className="p-3 bg-murram-100 rounded-lg">
                                <Mail className="h-6 w-6 text-murram-500" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters & Search */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-soil-100">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0 md:space-x-4">
                        {/* Search */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-soil-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by name, city, or neighborhood..."
                                className="w-full pl-10 pr-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="flex items-center space-x-2">
                            <Filter className="h-5 w-5 text-soil-400" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                            >
                                <option value="all">All Statuses</option>
                                {Object.values(PROPERTY_STATUSES).map((status) => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Properties Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                            <p className="text-soil-600">Loading properties...</p>
                        </div>
                    </div>
                ) : filteredProperties.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredProperties.map((property) => (
                            <PropertyCard
                                key={property.propertyId || property.property_id}
                                property={property}
                                onEdit={handleEditProperty}
                                onDelete={handleDeleteProperty}
                                onPublish={handlePublishProperty}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <Home className="mx-auto h-12 w-12 text-soil-400 mb-4" />
                        <h3 className="text-lg font-medium text-soil-900 mb-2">
                            {searchQuery || statusFilter !== 'all'
                                ? 'No properties found'
                                : 'No properties yet'}
                        </h3>
                        <p className="text-soil-600 mb-6">
                            {searchQuery || statusFilter !== 'all'
                                ? 'Try adjusting your search or filter'
                                : 'Get started by creating your first property listing'}
                        </p>
                        {!searchQuery && statusFilter === 'all' && (
                            <button
                                onClick={handleCreateProperty}
                                className="inline-flex items-center px-4 py-2 bg-highland-500 text-white rounded-lg font-medium hover:bg-highland-600 transition-colors"
                            >
                                <Plus className="h-5 w-5 mr-2" />
                                Create Your First Property
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, property: null })}
                onConfirm={confirmDelete}
                propertyName={deleteModal.property?.propertyName || deleteModal.property?.property_name}
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default PropertyDashboard;
